/**
 * Sheet 06 — signup (A.1, A.2, T.1, T.5; FR-002, FR-020; research §1.13).
 *
 * One form creates the tenant and its first identity. The T.5 disclosure
 * renders verbatim from mandatory-copy.ts above Continue and is never
 * dismissible (callout 7, CLAUDE.md §3.5). The business-email check runs on
 * blur against the deny-list served by `GET /v1/public/signup-policy`, with the
 * policy's own `business_email_message`, so the on-blur text and the server's
 * 400 text are one string (F24). Continue is disabled, with its reason stated,
 * until every field is valid and the terms are ticked (callouts 8, 12; §11).
 *
 * Callouts 3 and 9 are superseded: no verification email is sent. A 201 sets
 * the pre-login cookie and the browser goes to /mfa; the waiting screen comes
 * after enrolment. The cached tenant query is dropped first so the /mfa gate
 * refetches instead of acting on the stale 401 (see AuthGate).
 *
 * Mission 9 WP14 T088 (FR-019; OR-13, U5): the metering statement (`MeteringStatement`) sits above
 * the T.5 disclosure; nothing else on the page changes.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import {
  PROBLEM,
  fieldErrors,
  getSignupPolicy,
  isProblem,
  signup,
  type SignupPolicy,
} from '@/lib/auth'
import { T5_DISCLOSURE } from '@/lib/mandatory-copy'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DisclaimerBand } from '@/components/trellis/DisclaimerBand'
import { clearTenantQuery } from '@/components/trellis/AuthGate'
import { MeteringStatement } from './subscribe/MeteringStatement'
import { AuthLayout, DisabledReason, FieldError, failureCopy } from './AuthLayout'

export const SIGNUP_POLICY_QUERY_KEY = ['signup-policy'] as const

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when the address's domain is on the served deny-list (case-insensitive). */
export function isDeniedDomain(email: string, policy: SignupPolicy | undefined): boolean {
  const domain = email.trim().toLowerCase().split('@')[1]
  if (!domain || !policy) return false
  return policy.business_email_denylist.some((d) => d.toLowerCase() === domain)
}

type Field = 'email' | 'password' | 'legal_name' | 'reports_today'
const FIELDS: readonly Field[] = ['email', 'password', 'legal_name', 'reports_today']

export default function Signup() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const policyQuery = useQuery({
    queryKey: SIGNUP_POLICY_QUERY_KEY,
    queryFn: getSignupPolicy,
    staleTime: 5 * 60_000,
  })
  const policy = policyQuery.data
  const minLength = policy?.password_min_length ?? 12

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [legalName, setLegalName] = useState('')
  const [reportsToday, setReportsToday] = useState<boolean | null>(null)
  const [terms, setTerms] = useState(false)
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({})
  const [serverErrors, setServerErrors] = useState<Partial<Record<Field, string>>>({})
  const [formError, setFormError] = useState<ReactNode>(null)
  const [submitting, setSubmitting] = useState(false)

  const ids = {
    email: useId(),
    password: useId(),
    legal_name: useId(),
    terms: useId(),
    reason: useId(),
  }

  const denied = isDeniedDomain(email, policy)
  const emailShapeOk = EMAIL_SHAPE.test(email.trim())
  const passwordOk = password.length >= minLength
  const legalNameOk = legalName.trim().length > 0
  const fieldsComplete = emailShapeOk && passwordOk && legalNameOk && reportsToday !== null

  const clientError: Partial<Record<Field, string>> = {}
  if (touched.email && email && !emailShapeOk) clientError.email = 'Enter a valid email address.'
  if (touched.email && denied && policy) clientError.email = policy.business_email_message
  if (touched.password && password && !passwordOk)
    clientError.password = `Use at least ${minLength} characters.`
  if (touched.legal_name && !legalNameOk) clientError.legal_name = 'Enter the legal name of your organization.'

  const errorFor = (f: Field) => serverErrors[f] ?? clientError[f]

  const reason = !fieldsComplete
    ? 'Continue is unavailable until you complete every field.'
    : denied
      ? 'Continue is unavailable until you use a business email address.'
      : !policy
        ? 'Continue is unavailable until the page finishes loading.'
        : !terms
          ? 'Continue is unavailable until you accept the terms.'
          : null
  const canSubmit = reason === null && !submitting

  function touch(f: Field) {
    setTouched((t) => ({ ...t, [f]: true }))
  }

  function edit<T>(f: Field, set: (v: T) => void) {
    return (v: T) => {
      set(v)
      setServerErrors((e) => ({ ...e, [f]: undefined }))
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit || !policy || reportsToday === null) return
    setSubmitting(true)
    setFormError(null)
    try {
      const res = await signup({
        email: email.trim(),
        password,
        legal_name: legalName.trim(),
        reports_today: reportsToday,
        terms_version: policy.terms_version,
        accept_terms: true,
      })
      clearTenantQuery(queryClient)
      navigate('/mfa', { state: { next: res.next } })
    } catch (err) {
      if (isProblem(err, PROBLEM.ACCOUNT_EXISTS)) {
        setFormError(
          <>
            An account already exists for this address.{' '}
            <Link to="/login" className="text-[var(--color-link)] underline">
              Log in
            </Link>{' '}
            instead, or reset your password if you have lost it.
          </>,
        )
      } else if (isProblem(err, PROBLEM.VALIDATION_FAILED)) {
        const next: Partial<Record<Field, string>> = {}
        const rest: string[] = []
        for (const fe of fieldErrors(err)) {
          if ((FIELDS as readonly string[]).includes(fe.field)) next[fe.field as Field] = fe.message
          else rest.push(fe.message)
        }
        setServerErrors(next)
        setTouched({ email: true, password: true, legal_name: true, reports_today: true })
        if (rest.length > 0) setFormError(rest.join(' '))
      } else {
        setFormError(failureCopy(err))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const describedBy = (f: Field, helpId?: string) =>
    [errorFor(f) ? `${ids[f as keyof typeof ids]}-error` : null, helpId ?? null].filter(Boolean).join(' ') ||
    undefined

  return (
    <AuthLayout
      title="Create your account"
      lede="Trellis is self-service: your organization becomes a credit data reporter under its own bureau credentials."
    >
      {policyQuery.isError ? (
        <Alert variant="blocked">
          <AlertTitle>Trellis could not load the signup page</AlertTitle>
          <AlertDescription>
            <p>Check your connection and try again.</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void policyQuery.refetch()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.email}>
              Business email <span className="font-normal text-navy-600">Required</span>
            </Label>
            <Input
              id={ids.email}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => edit('email', setEmail)(e.target.value)}
              onBlur={() => touch('email')}
              aria-invalid={errorFor('email') ? true : undefined}
              aria-describedby={describedBy('email', `${ids.email}-help`)}
            />
            {errorFor('email') && <FieldError id={`${ids.email}-error`}>{errorFor('email')}</FieldError>}
            <p id={`${ids.email}-help`} className="text-caption text-navy-600">
              Use the address you log in with. Consumer mail providers are not accepted.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.password}>
              Password <span className="font-normal text-navy-600">Required</span>
            </Label>
            <Input
              id={ids.password}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => edit('password', setPassword)(e.target.value)}
              onBlur={() => touch('password')}
              aria-invalid={errorFor('password') ? true : undefined}
              aria-describedby={describedBy('password', `${ids.password}-help`)}
            />
            {errorFor('password') && <FieldError id={`${ids.password}-error`}>{errorFor('password')}</FieldError>}
            <p id={`${ids.password}-help`} className="text-caption text-navy-600">
              At least {minLength} characters. You will also set up an authenticator app next.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.legal_name}>
              Legal name of your organization <span className="font-normal text-navy-600">Required</span>
            </Label>
            <Input
              id={ids.legal_name}
              autoComplete="organization"
              value={legalName}
              onChange={(e) => edit('legal_name', setLegalName)(e.target.value)}
              onBlur={() => touch('legal_name')}
              aria-invalid={errorFor('legal_name') ? true : undefined}
              aria-describedby={describedBy('legal_name', `${ids.legal_name}-help`)}
            />
            {errorFor('legal_name') && (
              <FieldError id={`${ids.legal_name}-error`}>{errorFor('legal_name')}</FieldError>
            )}
            <p id={`${ids.legal_name}-help`} className="text-caption text-navy-600">
              This is the party bound by the terms and named on every file you report.
            </p>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-body-sm font-medium text-navy-950">
              Do you report to the credit bureaus today?
            </legend>
            <div className="flex gap-6">
              {(
                [
                  [true, 'Yes'],
                  [false, 'No'],
                ] as const
              ).map(([value, label]) => (
                <label key={label} className="flex items-center gap-2 text-body text-navy-950">
                  <input
                    type="radio"
                    name="reports_today"
                    className="size-[18px] accent-navy-950"
                    checked={reportsToday === value}
                    onChange={() => edit('reports_today', setReportsToday)(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
            {errorFor('reports_today') && (
              <FieldError id={`${ids.reason}-reports`}>{errorFor('reports_today')}</FieldError>
            )}
          </fieldset>

          <div className="flex items-start gap-2">
            <Checkbox
              id={ids.terms}
              checked={terms}
              onCheckedChange={(v) => setTerms(v === true)}
              className="mt-0.5"
            />
            {/* T.1, counsel memo 2026-10-03 B5 (provisional): verbatim, the version the one the policy serves. */}
            <Label htmlFor={ids.terms} className="block text-body font-normal" data-signup-acceptance="">
              By creating an account, you accept the{' '}
              <Link to="/legal/terms" className="text-[var(--color-link)] underline">
                Terms of Service
              </Link>
              {policy ? ` (version ${policy.terms_version})` : ''},{' '}
              <Link to="/legal/privacy" className="text-[var(--color-link)] underline">
                Privacy Notice
              </Link>
              , and{' '}
              <Link to="/legal/dpa" className="text-[var(--color-link)] underline">
                Data Processing Agreement
              </Link>{' '}
              on behalf of your organization. You acknowledge that Trellis is a self-service tool: you prepare, check,
              and deliver your credit reporting files under your own bureau credentials, and your organization is
              solely responsible for the accuracy and legal compliance of all data reported.
            </Label>
          </div>

          {/* Mission 9 WP14 T088 (FR-019; OR-13): what a credit meters, the one component, above T.5. */}
          <MeteringStatement />

          {/* T.5 — verbatim, never dismissible, before Continue (callout 7). */}
          <DisclaimerBand>{T5_DISCLOSURE}</DisclaimerBand>

          {formError && (
            <Alert variant="blocked">
              <AlertDescription>
                <p>{formError}</p>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit}
              aria-busy={submitting || undefined}
              aria-describedby={reason ? ids.reason : undefined}
            >
              Continue
            </Button>
            {reason && <DisabledReason id={ids.reason}>{reason}</DisabledReason>}
          </div>

          <p className="text-body-sm text-navy-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[var(--color-link)] hover:underline">
              Log in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  )
}
